import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Step {
    target: string;
    title: string;
    content: string;
    position?: 'top' | 'bottom' | 'left' | 'right';
}

interface OnboardingTourProps {
    steps: Step[];
    onComplete: () => void;
    storageKey?: string;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({
    steps,
    onComplete,
    storageKey = 'onboarding_completed'
}) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if onboarding was already completed
        const completed = localStorage.getItem(storageKey);
        if (!completed) {
            // Delay start for smoother UX
            setTimeout(() => setIsVisible(true), 1000);
        }
    }, [storageKey]);

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleComplete();
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSkip = () => {
        handleComplete();
    };

    const handleComplete = () => {
        localStorage.setItem(storageKey, new Date().toISOString());
        setIsVisible(false);
        onComplete();
    };

    if (!isVisible) return null;

    const step = steps[currentStep];
    const progress = ((currentStep + 1) / steps.length) * 100;

    return (
        <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Content Card */}
            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto">
                <div className="module-card p-6 border-primary/30 shadow-2xl">
                    {/* Progress Bar */}
                    <div className="w-full h-1 bg-surface-2 rounded-full mb-4 overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    {/* Step Counter */}
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs text-text-secondary">
                            {t('onboarding.step', 'Étape')} {currentStep + 1} / {steps.length}
                        </span>
                        <button
                            onClick={handleSkip}
                            className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                        >
                            {t('onboarding.skip', 'Passer le tutoriel')}
                        </button>
                    </div>

                    {/* Step Content */}
                    <div className="mb-6">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 mx-auto">
                            <span className="material-symbols-outlined text-primary text-2xl">
                                lightbulb
                            </span>
                        </div>
                        <h3 className="text-xl font-bold text-text-primary text-center mb-2">
                            {t(`onboarding.steps.${step.target}.title`, step.title)}
                        </h3>
                        <p className="text-text-secondary text-center text-sm leading-relaxed">
                            {t(`onboarding.steps.${step.target}.content`, step.content)}
                        </p>
                    </div>

                    {/* Navigation */}
                    <div className="flex gap-3">
                        {currentStep > 0 && (
                            <button
                                onClick={handlePrevious}
                                className="flex-1 px-4 py-3 bg-surface-2 text-text-primary font-bold rounded-xl hover:bg-surface-3 transition-colors flex items-center justify-center gap-2"
                            >
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('onboarding.prev', 'Précédent')}
                            </button>
                        )}
                        <button
                            onClick={handleNext}
                            className="flex-1 px-4 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
                        >
                            {currentStep < steps.length - 1 ? (
                                <>
                                    {t('onboarding.next', 'Suivant')}
                                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                                </>
                            ) : (
                                <>
                                    {t('onboarding.start', 'Commencer')}
                                    <span className="material-symbols-outlined text-sm">check</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Default onboarding steps for the app
export const DEFAULT_ONBOARDING_STEPS: Step[] = [
    {
        target: 'home',
        title: 'Bienvenue sur Padel Brothers ! 🎾',
        content: 'L\'application communautaire pour organiser vos matchs de padel entre amis.'
    },
    {
        target: 'groups',
        title: 'Rejoignez un Groupe',
        content: 'Commencez par rejoindre ou créer un groupe pour accéder aux fonctionnalités de matchmaking et tournois.'
    },
    {
        target: 'matchmaking',
        title: 'Trouvez des Partenaires',
        content: 'Utilisez le matchmaking pour trouver des joueurs disponibles et organiser des matchs rapidement.'
    },
    {
        target: 'challenges',
        title: 'Lancez des Défis',
        content: 'Défiez d\'autres joueurs et suivez vos confrontations directes pour le classement.'
    },
    {
        target: 'tournaments',
        title: 'Participez aux Tournois',
        content: 'Créez ou rejoignez des tournois pour des compétitions plus structurées avec différents formats.'
    },
    {
        target: 'profile',
        title: 'Votre Profil',
        content: 'Consultez vos statistiques, badges et classement. Personnalisez votre profil et suivez vos progrès !'
    }
];
